import { AttachFile as AttachFileIcon } from '@mui/icons-material';
import {
  Box,
  Chip,
  LinearProgress,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TableSortLabel,
} from '@mui/material';
import { memo } from 'react';

import { supportMessageLib } from 'entities/support-message';

import { ESupportMessageStatus, Pagination, SupportMessageListItem } from 'shared/api';

type Column = {
  id: string;
  label: string;
  sortable: boolean;
  align?: 'left' | 'right' | 'center';
};

const columns: Column[] = [
  { id: 'subject', label: 'Subject', sortable: true, align: 'left' },
  { id: 'email', label: 'Email', sortable: true, align: 'left' },
  { id: 'language', label: 'Language', sortable: true, align: 'left' },
  { id: 'status', label: 'Status', sortable: true, align: 'center' },
  { id: 'createdAt', label: 'Date', sortable: true, align: 'left' },
  { id: 'attachmentsCount', label: 'Attachments', sortable: false, align: 'center' },
];

const statusConfig: Record<ESupportMessageStatus, { label: string; color: 'error' | 'warning' | 'success' }> = {
  [ESupportMessageStatus.New]: { label: 'New', color: 'error' },
  [ESupportMessageStatus.Read]: { label: 'Read', color: 'warning' },
  [ESupportMessageStatus.Replied]: { label: 'Replied', color: 'success' },
};

const formatDate = (dateString: string): string => {
  const date = new Date(dateString);
  return date.toLocaleDateString('en-GB', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

type Props = {
  list: SupportMessageListItem[];
  pagination: Pagination | null;
  page: number;
  rowsPerPage: number;
  sortBy: string;
  sortOrder: 'asc' | 'desc';
  isLoading?: boolean;
  onPageChange: (newPage: number) => void;
  onRowsPerPageChange: (newRowsPerPage: number) => void;
  onSortChange: (sortBy: string) => void;
  onRowClick: (id: string) => void;
};

export const SupportMessagesTable = memo(
  ({
    list,
    pagination,
    page,
    rowsPerPage,
    sortBy,
    sortOrder,
    isLoading = false,
    onPageChange,
    onRowsPerPageChange,
    onSortChange,
    onRowClick,
  }: Props) => {
    const handleChangePage = (event: unknown, newPage: number) => {
      onPageChange(newPage + 1);
    };

    const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
      onRowsPerPageChange(parseInt(event.target.value, 10));
    };

    const handleSortRequest = (columnId: string) => {
      onSortChange(columnId);
    };

    return (
      <Paper sx={{ width: '100%', overflow: 'hidden' }}>
        <Box sx={{ width: '100%', height: 4 }}>{isLoading && <LinearProgress />}</Box>

        <Box sx={{ opacity: isLoading ? 0.6 : 1, transition: 'opacity 0.2s' }}>
          <TableContainer sx={{ maxHeight: 'calc(100vh - 305px)' }}>
            <Table stickyHeader aria-label="support messages table">
              <TableHead>
                <TableRow>
                  {columns.map((column) => (
                    <TableCell key={column.id} align={column.align}>
                      {column.sortable ? (
                        <TableSortLabel
                          active={sortBy === column.id}
                          direction={sortBy === column.id ? sortOrder : 'asc'}
                          onClick={() => handleSortRequest(column.id)}
                        >
                          {column.label}
                        </TableSortLabel>
                      ) : (
                        column.label
                      )}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {list && list.length > 0 ? (
                  list.map((message) => {
                    const config = statusConfig[message.status];
                    return (
                      <TableRow
                        key={message._id}
                        sx={{
                          '&:last-child td, &:last-child th': { border: 0 },
                          '&.MuiTableRow-hover:hover': { cursor: 'pointer' },
                        }}
                        hover
                        onClick={() => onRowClick(message._id)}
                      >
                        <TableCell align="left">{supportMessageLib.getSupportSubjectText(message.subject)}</TableCell>
                        <TableCell align="left">{message.email}</TableCell>
                        <TableCell align="left">{message.language ?? '—'}</TableCell>
                        <TableCell align="center">
                          <Chip label={config.label} color={config.color} size="small" />
                        </TableCell>
                        <TableCell align="left">{formatDate(message.createdAt)}</TableCell>
                        <TableCell align="center">
                          {message.attachmentsCount > 0 ? (
                            <Box display="inline-flex" alignItems="center" gap={0.5}>
                              <AttachFileIcon fontSize="small" color="action" />
                              {message.attachmentsCount}
                            </Box>
                          ) : (
                            <Box component="span" sx={{ color: 'text.disabled' }}>
                              —
                            </Box>
                          )}
                        </TableCell>
                      </TableRow>
                    );
                  })
                ) : (
                  <TableRow>
                    <TableCell colSpan={6} align="center">
                      No messages found
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>

        {pagination && (
          <TablePagination
            rowsPerPageOptions={[10, 20, 50]}
            component="div"
            count={pagination.total}
            rowsPerPage={rowsPerPage}
            page={page - 1}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            labelRowsPerPage="Rows per page:"
            labelDisplayedRows={({ from, to, count }) => `${from}-${to} of ${count}`}
            sx={{ borderTop: 1, borderColor: 'divider' }}
          />
        )}
      </Paper>
    );
  },
);

SupportMessagesTable.displayName = 'SupportMessagesTable';
